import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaUndo } from 'react-icons/fa';
import { theme } from '../common/theme';
import ScoreControls from './ScoreControls';

const Scoreboard = ({ score, onUpdateScore, onResetScore, canUpdate, isLoading }) => {
  const { teamAScore, teamBScore, teamAName, teamBName, status } = score;

  const handleScoreUpdate = (team) => {
    if (!canUpdate || isLoading || status !== 'ACTIVE') return;
    onUpdateScore(team);
  };

  const handleReset = () => {
    if (!canUpdate || isLoading || status !== 'ACTIVE') return;
    if (window.confirm('Are you sure you want to reset the score?')) {
      onResetScore();
    }
  };

  if (isLoading) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  if (status === 'ERROR') {
    return <ErrorContainer>Failed to load scoreboard</ErrorContainer>;
  }

  if (status === 'ENDED') {
    return (
      <Container>
        <FinalScore>
          <TeamName>{teamAName}</TeamName>
          <Score>{teamAScore}</Score>
          <Divider>Final</Divider>
          <TeamName>{teamBName}</TeamName>
          <Score>{teamBScore}</Score>
        </FinalScore>
      </Container>
    );
  }

  return (
    <Container>
      <TeamSection>
        <TeamName>{teamAName}</TeamName>
        <Score>{teamAScore}</Score>
      </TeamSection>

      <Divider>
        {canUpdate && status === 'ACTIVE' && (
          <ResetButton onClick={handleReset} title="Reset Score">
            <FaUndo />
          </ResetButton>
        )}
        VS
      </Divider>

      <TeamSection>
        <TeamName>{teamBName}</TeamName>
        <Score>{teamBScore}</Score>
      </TeamSection>

      {canUpdate && status === 'ACTIVE' && !score.isGameEnded && (
        <ScoreControls
          onUpdateScore={handleScoreUpdate}
          disabled={isLoading}
        />
      )}
    </Container>
  );
};

Scoreboard.propTypes = {
  score: PropTypes.shape({
    teamAScore: PropTypes.number.isRequired,
    teamBScore: PropTypes.number.isRequired,
    teamAName: PropTypes.string.isRequired,
    teamBName: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['INITIALIZING', 'ACTIVE', 'ENDED', 'ERROR']).isRequired,
    isGameEnded: PropTypes.bool,
    error: PropTypes.string
  }).isRequired,
  onUpdateScore: PropTypes.func.isRequired,
  onResetScore: PropTypes.func.isRequired,
  canUpdate: PropTypes.bool,
  isLoading: PropTypes.bool
};

Scoreboard.defaultProps = {

  isLoading: false
};

export default Scoreboard;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2.5rem;
  padding: 0.4rem 0.8rem;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0.5rem;
  font-weight: 600;
  pointer-events: none;
  color: #fff;

  @media (min-width: 900px) {
    .score {
      font-size: 2rem;
    }
  }
`;

const TeamSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TeamName = styled.span`
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const Score = styled.div`
  font-size: 1.6rem;
  min-width: 2.6rem;
  text-align: center;
  font-weight: 700;
`;

const Divider = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -1.25rem;
    right: -1.25rem;
    height: 1px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  pointer-events: auto;

  &:hover:not(:disabled) {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const FinalScore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.errorLight || 'rgba(255, 0, 0, 0.1)'};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  width: 100%;
`; 